import React from 'react';
import { Flex, Box, Text } from 'rebass';
import {
  ButtonSocialLinkedIn,
  ButtonSocialFacebook,
  ButtonSocialGoogle,
} from '../buttons';
import { CautionIcon } from '../icons';
import { TextProps } from 'rebass';

interface SocialLoginButtonsProps {
  onClickLinkedIn?: () => void;
  onClickGoogle?: () => void;
  onClickFacebook?: () => void;
  isSignUp?: boolean;
}

export const SocialLoginButtons = ({
  onClickLinkedIn,
  onClickGoogle,
  onClickFacebook,
  isSignUp,
}: SocialLoginButtonsProps) => (
  <Box my="10px">
    <ButtonSocialLinkedIn onClick={onClickLinkedIn} isSignUp={isSignUp} />
    <Box height="10px" />
    <ButtonSocialGoogle onClick={onClickGoogle} isSignUp={isSignUp} />
    <Box height="10px" />
    <ButtonSocialFacebook onClick={onClickFacebook} isSignUp={isSignUp} />
  </Box>
);

export const OrDivider = () => (
  <Flex justifyContent="center" alignItems="center" my="22px">
    <Box width="100%" height="1px" bg="rgba(168, 151, 134, 0.3)" mr="5px" />
    <Text
      fontFamily="label"
      fontSize="18px"
      fontWeight="bold"
      lineHeight={1.22}
      color="rgba(32, 37, 41, 0.5)"
      width="55px"
    >
      OR
    </Text>
    <Box width="100%" height="1px" bg="rgba(168, 151, 134, 0.3)" ml="5px" />
  </Flex>
);

export const LoginLink = ({
  onLoginLinkClicked = () => {},
}: {
  onLoginLinkClicked?: () => void;
}) => (
  <Flex alignSelf="center">
    <Text
      fontFamily="label"
      fontSize="16px"
      lineHeight={1.75}
      color="rgba(0, 0, 0, 0.5)"
    >
      Already have an account?
    </Text>
    <Box
      as="a"
      href="/login"
      onClick={e => {
        e.preventDefault();
        onLoginLinkClicked();
      }}
      css={{ color: '#13284c', fontSize: '16px' }}
    >
      <Text
        ml="5px"
        fontFamily="label"
        fontSize="16px"
        fontWeight="bold"
        lineHeight={1.75}
        color="blue"
      >
        Log in
      </Text>
    </Box>
  </Flex>
);

export const SignUpLink = ({
  onSignUpLinkClicked = () => {},
}: {
  onSignUpLinkClicked?: () => void;
}) => (
  <Flex alignSelf="center">
    <Text
      fontFamily="label"
      fontSize="16px"
      lineHeight={1.75}
      color="rgba(0, 0, 0, 0.5)"
    >
      Don't have an account yet?
    </Text>
    <Box
      as="a"
      href="/sign-up"
      onClick={e => {
        e.preventDefault();
        onSignUpLinkClicked();
      }}
      css={{ color: '#13284c', fontSize: '16px' }}
    >
      <Text
        ml="5px"
        fontFamily="label"
        fontSize="16px"
        fontWeight="bold"
        lineHeight={1.75}
        color="blue"
      >
        Sign up
      </Text>
    </Box>
  </Flex>
);

interface ErrorNotificationProps {
  errorMessage: string;
}

export const ErrorNotification = ({ errorMessage }: ErrorNotificationProps) => (
  <Flex css={{ border: 'solid 1px #e0182d', borderLeft: 'solid 5px #e0182d' }}>
    <Box ml="20px" mr="20px" mt="12px">
      <CautionIcon height="17px" width="17px" />
    </Box>
    <Text
      my="11px"
      mx="10px"
      fontFamily="label"
      fontSize="14px"
      lineHeight={1.29}
      color="#1c273f"
    >
      {errorMessage}
    </Text>
  </Flex>
);

export const CreateAnAccount = ({
  onClick,
  userName,
}: {
  onClick: () => void;
  userName: string;
}) => (
  <Flex alignSelf="center">
    <Text fontFamily="label" fontSize="16px" color="rgba(0, 0, 0, 0.5)">
      {`Not ${userName}?`}
    </Text>
    <Text
      onClick={onClick}
      ml="5px"
      fontFamily="label"
      fontSize="16px"
      color="blue"
      css={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      Create an account
    </Text>
  </Flex>
);

export const TermsAndConditions = () => (
  <Text
    fontFamily="body"
    maxWidth="290px"
    fontSize="14px"
    color="rgba(0, 0, 0, 0.5)"
    textAlign="center"
  >
    By signing up, you agree to MDLinx’s{' '}
    <a
      href="https://www.mdlinx.com/terms-of-use/"
      target="_blank"
      style={{ color: 'rgba(0, 0, 0, 0.5)' }}
    >
      Terms of Use
    </a>{' '}
    and{' '}
    <a
      href="https://www.mdlinx.com/privacy-policy/"
      target="_blank"
      style={{ color: 'rgba(0, 0, 0, 0.5)' }}
    >
      Privacy Policy
    </a>
  </Text>
);

interface FormHeaderProps extends TextProps {
  children: string | React.ReactNode;
}

export const FormHeader = (props: FormHeaderProps) => (
  <Text
    fontFamily="label"
    fontSize="24px"
    fontWeight={500}
    letterSpacing="-0.45px"
    color="#212529"
  >
    {props.children}
  </Text>
);

interface FormSubHeaderProps extends TextProps {
  children: string | React.ReactNode;
}

export const FormSubHeader = (props: FormSubHeaderProps) => (
  <Text
    fontFamily="body"
    fontSize="16px"
    lineHeight={1.31}
    color="rgba(0, 0, 0, 0.5)"
    {...props}
    maxWidth="100%"
  >
    {props.children}
  </Text>
);

export const SmallDivider = () => (
  <Box
    width="140px"
    height="1px"
    bg="rgba(168, 151, 134, 0.3)"
    alignSelf="center"
    mt="20px"
    mb="24px"
  />
);

interface BackToLoginProps {
  onClick?: () => void;
}

export const BackToLogin = ({ onClick }: BackToLoginProps) => (
  <Flex justifyContent="center" onClick={onClick}>
    <Text
      ml="5px"
      fontFamily="label"
      fontSize="16px"
      color="blue"
      css={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {'< Back to Log in'}
    </Text>
  </Flex>
);

interface ActionTextProps {
  onClick?: () => void;
  children: string;
}

export const ActionText = ({ onClick, children }: ActionTextProps) => (
  <Flex justifyContent="center" alignItems="center" onClick={onClick}>
    <Text
      as="a"
      fontFamily="label"
      fontSize="16px"
      color="blue"
      textAlign="center"
      maxWidth="290px"
      css={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {children}
    </Text>
  </Flex>
);
